import { QueryFeatures, SearchMode } from "helpers/api";
import { CitationType } from "helpers/cite";

/**
===============================================================================
START UPDATE IF CHANGED WARNING ===============================================
===============================================================================
common/src/python/common/analytics/mixpanel.py
===============================================================================
*/
export enum MixpanelEvent {
  AnalysisExport = "Analysis Export",
  AskPaperClick = "Ask Paper Click",
  AskPaperChat = "Ask Paper Chat",
  Cancellation = "Cancellation",
  ClaimShare = "Claim Share",
  ClaimView = "Claim View",
  CreateList = "Create List",
  ExportResults = "Export Results",
  FullTextClick = "Full Text Click",
  LogIn = "User Log In",
  LogOut = "User Log Out",
  NewSearchModeSelected = "New Search Mode Selected",
  PageView = "Page View",
  PageViewBrowser = "Page View - Browser",
  PaperCite = "Paper Cite",
  PaperSave = "Paper Save",
  PaperShare = "Paper Share",
  PaperView = "Paper View",
  ProAnalysisCopy = "Pro Analysis Copy",
  SummaryCopy = "Summary Copy",
  RelatedSearchClick = "Related Search Click",
  SaveSearch = "Save Search",
  Search = "Search",
  SearchResultsItemClick = "Results Item Click",
  SearchResultsShare = "Results Share",
  SearchResultsView = "Results View",
  SignUp = "User Sign Up",
  StudySnapshot = "Study Snapshot",
  Subscribe = "Subscribe",
  SubscriptionModified = "Subscription Modified",
  ToggleProAnalysis = "Toggle Pro Analysis",
  ToggleYesNoSummaryTable = "Toggle Yes No Summary Table",
  ToggleThreadShare = "Toggle Thread Share",
  ConsensusMeter = "Consensus Meter",
  UploadPaperTriggered = "Upload Paper - Triggered",
  UploadPaperSuccess = "Upload Paper - Success",
  UploadPaperError = "Upload Paper - Error",
  DeleteUploadedPaper = "Delete Uploaded Paper",
  DownloadUploadedPaper = "Download Uploaded Paper",
  DismissBanner = "Dismiss Banner",
  ClickResearchHubLink = "Click Research Hub Link",
  NewThread = "New Thread",
  NewThreadInteraction = "New Thread Interaction",
  ShareThread = "Share Thread",
  OpenShareThread = "Open Share Thread",
  ForkThread = "Fork Thread",
  ViewThread = "View Thread",
  AnnouncementBannerClick = "Announcement Banner Click",
  AnnouncementBannerDismiss = "Announcement Banner Dismiss",
  ClickStudentDiscountBanner = "Click Student Discount Banner",
  PaywallViewed = "Paywall Viewed",
  PaywallDismissed = "Paywall Dismissed",
  PaywallClicked = "Paywall Clicked",
  StudentDiscountModalViewed = "Student Discount Modal Viewed",
  StudentDiscountModalDismissed = "Student Discount Modal Dismissed",
  StudentDiscountModalClicked = "Student Discount Modal Clicked",
  OpenThreadPaperDrawer = "Open Thread Paper Drawer",
  SortThreadPapers = "Sort Thread Papers",
  ToggleDeepSearchStepDrawer = "Toggle Deep Search Step Drawer",
  OnboardingStarted = "Onboarding Started",
  OnboardingCompleted = "Onboarding Completed",
  ResultViewModeChanged = "Result View Mode Changed",
}

export enum MixpanelNavigationSource {
  PublicAPI = "Public API",
  ChatGPT = "ChatGPT",
  External = "External",
  LandingPage = "Landing Page",
  Website = "Website",
  RelatedSearch = "Related Search",
}

export enum MixpanelActionSource {
  BookmarkList = "Bookmark List",
  DetailsPage = "Details Page",
  SearchResults = "Search Results",
  Sidebar = "Sidebar",
  QuickSearch = "Quick Search",
  ProSearch = "Pro Search",
  DeepSearch = "Deep Search",
  CitationList = "Citation List",
  ReferenceList = "Reference List",
  ResearchHub = "Research Hub",
  ResearchHubAnnouncementBanner = "Research Hub Announcement Banner",
  ThreadBanner = "Thread Banner",
  AskPaper = "Ask Paper",
  Redirect = "Redirect",
  AskPaperResultTag = "Ask Paper Result Tag",
  UploadedPaperDetails = "Uploaded Paper Details",
  ResearchHubZeroStateEducation = "Research Hub Zero State Education",
  ThreadInteractionResults = "Thread Interaction Results",
  DataSourceSelector = "Data Source Selector",
  ThreadInteractionSourceResults = "Thread Interaction Source Results",
  ThreadInteractionProAnalysis = "Thread Interaction Pro Analysis",
  MainSearchBar = "Main Search Bar",
  HomePageSuggestedSearchFeed = "Home Page Feed",
  NewThreadSearchBar = "New Thread Search Bar",
  ThreadFollowUpSearchBar = "Thread Follow Up Search Bar",
  ApplyFiltersAndResend = "Apply Filters and Resend",
  NewSearchPage = "New Search Page", // Used to track when a new thread is created via the new search page indicating it was created from an old search
  ConsensusMeter = "Consensus Meter",
  SuggestedFollowUp = "Suggested Follow Up",
  UserAuthEvent = "User Auth Event",
  PremiumFeatureModal = "Premium Feature Modal",
  StudySnapshot = "Study Snapshot",
  ProToggle = "Pro Toggle",
  SearchModeSelector = "Search Mode Selector",
  ThreadPaperDrawer = "Thread Paper Drawer",
  SearchStepInfoBar = "Search Step Info Bar",
  PaperSidebarDrawer = "Paper Sidebar Drawer",
  Onboarding = "Onboarding",
  ResultsTableView = "Results Table View",
  ClaimBanner = "Claim Banner",
  PricingPage = "Pricing Page",
  SubscriptionPage = "Subscription Page",
}

export enum MixpanelPaperSource {
  ConsensusSearch = "Consensus Search",
  UserUpload = "User Upload",
}

export type MixpanelCiteType = CitationType;

export enum MixpanelFullTextLinkType {
  PDF = "PDF",
  LIBRARY = "LIBRARY",
  S2 = "S2",
  DOI = "DOI",
}

export enum MixpanelExportFileType {
  CSV = "CSV",
  RIS = "RIS",
}

export type ToggleType = "Open" | "Close";

/**
 * Major version tracking in Mixpanel
 * Naming convention: v[increasing integer]-datetime-[short descriptor]
 */
export enum MixpanelEventVersion {
  /**
   * Original claim search based product hunt launch, 20220908
   * Versions were launched with PaperSearch, so an event should also be
   * considered ClaimSearch if version is not set.
   */
  ClaimSearch = 1,
  /** Paper search launch, 20231031 */
  PaperSearch = 2,
  /** Research Hub launch */
  ResearchHub = 3,
  /** Threads launch */
  Threads = 4,
}

export interface MixpanelEventData {
  event: MixpanelEvent;
  version: MixpanelEventVersion;
}

export interface PlanProperties {
  value?: number;
  currency: string;
}

export interface PaperProperties {
  paperId: string | null;
  journalName: string | null;
  publishYear: number | null;
  paperTitle: string | null;
  firstAuthor: string | null;
  doi: string | null;
  resultText: string | null;
  resultType: "answer" | "takeaway" | null;
}

export interface SearchFilterProperties {
  studyTypes?: string;
  fieldsOfStudy?: string;
  countriesOfStudy?: string;
  // Bool filters
  openAccess?: string;
  hasValidChatPdf?: string;
  filterControlledStudies?: string;
  filterHumanStudies?: string;
  // Range filters
  citationsMin?: string;
  sampleSizeMin?: string;
  sampleSizeMax?: string;
  sjrBestQuartileMin?: string;
  sjrBestQuartileMax?: string;
  studyDurationDaysMin?: string;
  studyDurationDaysMax?: string;
  yearMin?: string;
  yearMax?: string;
  durationMax?: string;
  durationMin?: string;
  country?: string;
}

export interface ThreadProperties {
  threadId?: string;
  threadTitle?: string;
  threadInteractionId?: string;
  searchId?: string;
  threadInteractionIndex?: number;
  threadInteractionCount?: number;
}

export interface SearchSettingsProperties {
  proAnalysisOn?: boolean | null; // deprecated, use searchMode instead
  filters: SearchFilterProperties;
  filtersOn: boolean | null;
  query: string | null;
  searchMode?: SearchMode | null;
}

export interface SearchResultsProperties {
  adjustedQuery: string | null;
  error: string | null;
  isNullResults: boolean | null;
  summarySuccess?: boolean | null;
  queryFeatures: QueryFeatures;
}

// Events like Paper share require some but not all of the Search Properties.
export interface PaperEventSearchProperties {
  adjustedQuery: string | null;
  filters: SearchFilterProperties;
  query: string | null;
}

export interface ResultProperties {
  pageNumber: number | null;
  resultNumber: number | null;
}

/** User initiates cancelling their subscription. */
export interface CancellationEvent extends MixpanelEventData, PlanProperties {
  event: MixpanelEvent.Cancellation;
  plan?: string;
  status?: string;
  seats?: number;
}

/** User registers for Consensus. */
export interface SignUpEvent extends MixpanelEventData {
  event: MixpanelEvent.SignUp;
  authMethod?: string;
  createdAt?: number;
  signupAttribution?: any; // Attribution data from cookies
}

/** User views a query's results page, regardless of from a search or direct link. */
export interface SearchResultsViewEvent
  extends MixpanelEventData,
    SearchSettingsProperties,
    SearchResultsProperties,
    ThreadProperties {
  event: MixpanelEvent.SearchResultsView;
  navigationSource: MixpanelNavigationSource;
  pageNumber: number;
  isLoadMore: boolean;
  paperIds: string[];
  startRank: number;
}

/** User searches from the search bar. */
export interface SearchEvent
  extends MixpanelEventData,
    SearchSettingsProperties,
    ThreadProperties {
  event: MixpanelEvent.Search;
  navigationSource: MixpanelNavigationSource;
  isRelatedSearch?: boolean;
  isNewThread?: boolean;
  isThreadFollowUp?: boolean;
  actionSource?: MixpanelActionSource;
}

interface SaveSearchEvent
  extends MixpanelEventData,
    SearchSettingsProperties,
    SearchResultsProperties {
  event: MixpanelEvent.SaveSearch;
}

/** User exports search results. */
export interface ExportResultsEvent
  extends MixpanelEventData,
    SearchSettingsProperties,
    SearchResultsProperties,
    ThreadProperties {
  event: MixpanelEvent.ExportResults;
  actionSource?: MixpanelActionSource;
  fileType: MixpanelExportFileType;
  sortBy?: string;
}

/** User views a paper details page, regardless of from a search or direct link. */
interface PaperViewEvent extends MixpanelEventData, PaperProperties {
  event: MixpanelEvent.PaperView;
  navigationSource: MixpanelNavigationSource;
}

/** User sends a message to Ask Paper */
interface AskPaperChatEvent
  extends MixpanelEventData,
    PaperProperties,
    ResultProperties,
    PaperEventSearchProperties {
  event: MixpanelEvent.AskPaperChat;
  navigationSource: MixpanelNavigationSource;
  actionSource: MixpanelActionSource;
  message: string;
  paperSource: MixpanelPaperSource;
  paperId: string | null;
}

/** User clicks to open Ask Paper Drawer */
export interface AskPaperClickEvent
  extends MixpanelEventData,
    PaperProperties,
    ResultProperties,
    PaperEventSearchProperties,
    ThreadProperties {
  event: MixpanelEvent.AskPaperClick;
  navigationSource: MixpanelNavigationSource;
  actionSource: MixpanelActionSource;
  paperSource: MixpanelPaperSource;
  paperId: string;
}

/** User clicks full text link on share page. */
export interface FullTextClickEvent
  extends MixpanelEventData,
    PaperProperties,
    ResultProperties,
    PaperEventSearchProperties,
    ThreadProperties {
  event: MixpanelEvent.FullTextClick;
  linkType: MixpanelFullTextLinkType;
  libraryId?: string;
  actionSource: MixpanelActionSource;
}

/** User clicks one of the 4 share options. */
export interface SharePaperEvent
  extends MixpanelEventData,
    PaperProperties,
    ResultProperties,
    PaperEventSearchProperties,
    ThreadProperties {
  event: MixpanelEvent.PaperShare;
  actionSource: MixpanelActionSource;
  shareType: string;
}

interface SavePaperEvent
  extends MixpanelEventData,
    PaperProperties,
    ResultProperties,
    PaperEventSearchProperties,
    ThreadProperties {
  event: MixpanelEvent.PaperSave;
  actionSource: MixpanelActionSource;
}

export interface StudySnapshotEvent
  extends MixpanelEventData,
    PaperProperties,
    ResultProperties,
    PaperEventSearchProperties,
    ThreadProperties {
  event: MixpanelEvent.StudySnapshot;
  actionSource: MixpanelActionSource;
}

export interface CitePaperEvent
  extends MixpanelEventData,
    PaperProperties,
    ResultProperties,
    PaperEventSearchProperties,
    ThreadProperties {
  event: MixpanelEvent.PaperCite;
  actionSource: MixpanelActionSource;
  citeType: MixpanelCiteType;
}

/** User clicks a claim. */
export interface SearchResultsItemClickEvent
  extends MixpanelEventData,
    PaperProperties,
    ResultProperties,
    SearchSettingsProperties,
    SearchResultsProperties,
    ThreadProperties {
  event: MixpanelEvent.SearchResultsItemClick;
  actionSource: MixpanelActionSource;
}

/** User clicks a result from the Pro Analysis summary. */
export interface ProAnalysisResultsItemClickEvent
  extends SearchResultsItemClickEvent {
  referenceLineNumber: number;

  associatedSentence: string;
}

/** User copies the Pro Analysis text. */
export interface ProAnalysisCopyEvent
  extends MixpanelEventData,
    SearchSettingsProperties,
    SearchResultsProperties,
    ThreadProperties {
  event: MixpanelEvent.ProAnalysisCopy;
  citeType?: MixpanelCiteType;
}

/** User exports the Analysis text. */
export interface AnalysisExportEvent
  extends MixpanelEventData,
    SearchSettingsProperties,
    SearchResultsProperties,
    ThreadProperties {
  event: MixpanelEvent.AnalysisExport;
}

/** User copies the summary text. */
export interface SummaryCopyEvent
  extends MixpanelEventData,
    SearchSettingsProperties,
    SearchResultsProperties,
    ThreadProperties {
  event: MixpanelEvent.SummaryCopy;
}

/** User clicks a related search. */
export interface RelatedSearchClickEvent
  extends MixpanelEventData,
    ThreadProperties,
    SearchResultsProperties,
    SearchSettingsProperties {
  event: MixpanelEvent.RelatedSearchClick;
  relatedQuery: string;
  resultNumber: number;
  isQueryResubmit?: boolean; // Whether the related search is a resubmit of the original query when no results are found
  isYesNoQuestion?: boolean; // Whether the related search is a yes/no question
  pagePosition?: "top" | "bottom";
}

/** User shares results. */
interface SearchResultsShareEvent
  extends MixpanelEventData,
    SearchSettingsProperties,
    SearchResultsProperties {
  event: MixpanelEvent.SearchResultsShare;
  shareType: string;
  actionSource: MixpanelActionSource;
}

/** User succesfully logs in. */
interface LogInEvent extends MixpanelEventData {
  event: MixpanelEvent.LogIn;
  authMethod: string;
}

/** User succesfully logs out. */
interface LogOutEvent extends MixpanelEventData {
  event: MixpanelEvent.LogOut;
}

/** User views a page. */
interface PageViewEvent extends MixpanelEventData {
  event: MixpanelEvent.PageView;
  pageType: string;
  pageTitle: string;
}

interface CreateListEvent extends MixpanelEventData {
  event: MixpanelEvent.CreateList;
  listName: string;
}

export interface SubscriptionEventData
  extends MixpanelEventData,
    PlanProperties {
  billingType: string;
  plan: string;
  status?: string;
  seats?: number;
}

export interface SubscribeEventData extends SubscriptionEventData {
  event: MixpanelEvent.Subscribe;
}

export interface SubscriptionModifiedEventData extends SubscriptionEventData {
  event: MixpanelEvent.SubscriptionModified;
  // Previous state fields (current state is in base SubscriptionEventData)
  previousPlan?: string;
  previousBillingType?: string;
  previousValue?: number;
  // Seat changes for team subscriptions
  seatsAdded?: number;
  seatsRemoved?: number;
}

export interface ToggleProAnalysisEvent
  extends MixpanelEventData,
    PaperEventSearchProperties {
  event: MixpanelEvent.ToggleProAnalysis;
  value: boolean;
}

export interface ConsensusMeterEvent
  extends MixpanelEventData,
    SearchSettingsProperties,
    ThreadProperties {
  event: MixpanelEvent.ConsensusMeter;
  isYesNoQuestion: boolean;
  notEnoughResults: boolean;
  meterLoadSuccess: boolean;
}

export interface ToggleYesNoSummaryTableEvent
  extends MixpanelEventData,
    PaperEventSearchProperties {
  event: MixpanelEvent.ToggleYesNoSummaryTable;
  value: boolean;
}

export interface UploadPaperEvent extends MixpanelEventData {
  event: MixpanelEvent;
  actionSource: MixpanelActionSource;
  navigationSource?: MixpanelNavigationSource;
  errorMessage?: string;
}
export interface AskPaperEvent extends MixpanelEventData {
  event: MixpanelEvent;
  actionSource: MixpanelActionSource;
  navigationSource: MixpanelNavigationSource;
  paperId: string;
  paperSource: MixpanelPaperSource;
}

/** User toggles the thread share button. Value is true if thread is made public.
 * Value is false if thread is made private.
 */
export interface ToggleThreadShareEvent
  extends MixpanelEventData,
    ThreadProperties {
  event: MixpanelEvent.ToggleThreadShare;
  value: boolean;
}

export interface ShareThreadEvent extends MixpanelEventData, ThreadProperties {
  event: MixpanelEvent.ShareThread;
  shareType: string;
}

export interface OpenShareThreadEvent
  extends MixpanelEventData,
    ThreadProperties {
  event: MixpanelEvent.OpenShareThread;
}

export interface NewThreadEvent
  extends MixpanelEventData,
    SearchSettingsProperties {
  event: MixpanelEvent.NewThread;
  actionSource: MixpanelActionSource;
}

export interface NewSearchModeSelectedEvent extends MixpanelEventData {
  event: MixpanelEvent.NewSearchModeSelected;
  actionSource: MixpanelActionSource;
  searchMode: SearchMode;
}

export interface NewThreadInteractionEvent
  extends MixpanelEventData,
    ThreadProperties,
    SearchSettingsProperties {
  event: MixpanelEvent.NewThreadInteraction;
  actionSource: MixpanelActionSource;
}

export interface ForkThreadEvent
  extends MixpanelEventData,
    ThreadProperties,
    SearchSettingsProperties {
  event: MixpanelEvent.ForkThread;
  actionSource: MixpanelActionSource;
}

export interface ViewThreadEvent extends MixpanelEventData, ThreadProperties {
  event: MixpanelEvent.ViewThread;
  isOwner: boolean;
}

export interface AnnouncementBannerEvent extends MixpanelEventData {
  event:
    | MixpanelEvent.AnnouncementBannerClick
    | MixpanelEvent.AnnouncementBannerDismiss;
  feature: string;
}

/** Premium Feature Modal */
export interface PaywallViewedEvent extends MixpanelEventData {
  event: MixpanelEvent.PaywallViewed;
  actionSource: MixpanelActionSource;
}

export interface PaywallDismissedEvent extends MixpanelEventData {
  event: MixpanelEvent.PaywallDismissed;
  actionSource: MixpanelActionSource;
}

export interface PaywallClickedEvent extends MixpanelEventData {
  event: MixpanelEvent.PaywallClicked;
  actionSource: MixpanelActionSource;
  value: string; // the label of the button clicked in the paywall
}

/** Student Discount Banner */
export interface ClickStudentDiscountBannerEvent extends MixpanelEventData {
  event: MixpanelEvent.ClickStudentDiscountBanner;
  actionSource: MixpanelActionSource;
  isSignedIn: boolean;
}

/** Student Discount Modal */
export interface StudentDiscountModalViewedEvent extends MixpanelEventData {
  event: MixpanelEvent.StudentDiscountModalViewed;
  actionSource: MixpanelActionSource;
}

export interface StudentDiscountModalDismissedEvent extends MixpanelEventData {
  event: MixpanelEvent.StudentDiscountModalDismissed;
  actionSource: MixpanelActionSource;
}

export interface StudentDiscountModalClickedEvent extends MixpanelEventData {
  event: MixpanelEvent.StudentDiscountModalClicked;
  actionSource: MixpanelActionSource;
  value: string; // the label of the button clicked in the modal
}

/** Thread Paper Drawer */
export interface OpenThreadPaperDrawerEvent extends ThreadProperties {
  event: MixpanelEvent.OpenThreadPaperDrawer;
}

export interface SortThreadPapersEvent extends ThreadProperties {
  event: MixpanelEvent.SortThreadPapers;
  sortBy: string;
}

/** Deep Search Paper Drawer */
export interface ToggleDeepSearchStepDrawerEvent extends ThreadProperties {
  event: MixpanelEvent.ToggleDeepSearchStepDrawer;
  actionSource: MixpanelActionSource;
  type: ToggleType;
}

/** User starts onboarding flow */
export interface OnboardingStartedEvent extends MixpanelEventData {
  event: MixpanelEvent.OnboardingStarted;
}

/** User completes or skips onboarding */
export interface OnboardingCompletedEvent extends MixpanelEventData {
  event: MixpanelEvent.OnboardingCompleted;
  skipped: boolean;
  role?: string;
  other_role_text?: string;
  library_id?: string;
  library_name?: string;
  verified_student_email?: string;
  has_existing_student_email?: boolean;
}

/** User changes result view mode */
export interface ResultViewModeChangedEvent extends MixpanelEventData {
  event: MixpanelEvent.ResultViewModeChanged;
  resultViewMode: string;
}

export type MixpanelTrackEventData =
  | AnalysisExportEvent
  | AskPaperChatEvent
  | AskPaperClickEvent
  | AskPaperEvent
  | CancellationEvent
  | CitePaperEvent
  | CreateListEvent
  | ExportResultsEvent
  | FullTextClickEvent
  | LogInEvent
  | LogOutEvent
  | NewSearchModeSelectedEvent
  | PageViewEvent
  | PaperViewEvent
  | ProAnalysisCopyEvent
  | ProAnalysisResultsItemClickEvent
  | RelatedSearchClickEvent
  | SavePaperEvent
  | SaveSearchEvent
  | SearchEvent
  | SearchResultsItemClickEvent
  | SearchResultsShareEvent
  | SearchResultsViewEvent
  | SharePaperEvent
  | StudySnapshotEvent
  | SubscribeEventData
  | SubscriptionModifiedEventData
  | ToggleProAnalysisEvent
  | ToggleYesNoSummaryTableEvent
  | UploadPaperEvent
  | ToggleThreadShareEvent
  | ShareThreadEvent
  | OpenShareThreadEvent
  | NewThreadEvent
  | NewThreadInteractionEvent
  | ForkThreadEvent
  | ViewThreadEvent
  | SummaryCopyEvent
  | AnnouncementBannerEvent
  | OpenThreadPaperDrawerEvent
  | SortThreadPapersEvent
  | ToggleDeepSearchStepDrawerEvent
  | OnboardingStartedEvent
  | OnboardingCompletedEvent
  | ResultViewModeChangedEvent;

/**
===============================================================================
END UPDATE IF CHANGED WARNING =================================================
===============================================================================
*/
